import React from 'react';
import PropTypes from 'prop-types';
import * as moment from 'moment-timezone';
import translate from '../../translate';
import { dateWithoutTimeZone } from '../../../services/utilityFunctions';
import {
  getPlanTimezone,
  getStartDateAndEndDateInterval,
  isReportInterval,
} from '../../../services/dateUtilityFunctions';

/**
 * DateRange component wrapping the jQuery implementation,
 * see: http://www.daterangepicker.com/
 */
class DateRange extends React.Component {
  componentDidMount() {
    const {
      t,
      id,
      startDate: sDate,
      endDate: eDate,
      timePicker,
      onChange,
      onClear,
      customRanges,
      opens,
      directSellingSettings,
    } = this.props;
    const picker = $(`#${id}`);
    if (picker.length === 0) {
      return;
    }

    const start = sDate || moment();
    const end = eDate || moment();

    const today = t('dateRange_today');
    const yesterday = t('dateRange_yesterday');
    const sevenDays = t('dateRange_sevenDays');
    const thirtyDays = t('dateRange_thirtyDays');
    const thisMonth = t('dateRange_thisMonth');
    const lastMonth = t('dateRange_lastMonth');

    const defaultRanges = {
      [today]: [moment().utc().startOf('day'), moment().utc().endOf('day')],
      [yesterday]: [
        moment().utc().subtract(1, 'days').startOf('day'),
        moment().utc().subtract(1, 'days').endOf('day'),
      ],
      [sevenDays]: [
        moment().utc().subtract(6, 'days').startOf('day'),
        moment().utc().endOf('day').endOf('day'),
      ],
      [thirtyDays]: [
        moment().utc().subtract(29, 'days').startOf('day'),
        moment().utc().endOf('day').endOf('day'),
      ],
      [thisMonth]: getStartDateAndEndDateInterval(directSellingSettings),
      [lastMonth]: getStartDateAndEndDateInterval(
        directSellingSettings,
        moment().subtract(1, 'month'),
      ),
    };
    picker.daterangepicker({
      autoUpdateInput: false,
      locale: {
        applyLabel: t('dateRange_apply'),
        cancelLabel: t('dateRange_clear'),
      },
      timePicker,
      startDate: start,
      endDate: end,
      opens,
      drops: 'auto',
      ranges: customRanges || defaultRanges,
    });

    // If start date and end date exist, show in the input
    if (sDate && eDate) {
      const initialStartDate = moment(sDate);
      const initialEndDate = moment(eDate);
      let range = '';

      if (initialStartDate.isSame(initialEndDate, 'day')) {
        range = initialStartDate.format('YYYY MMM DD');
      } else {
        range = `${initialStartDate.format(
          'YYYY MMM DD',
        )} - ${initialEndDate.format('YYYY MMM DD')}`;
      }

      picker.find('.m-input').val(range);
    }

    picker.on('apply.daterangepicker', (ev, element) => {
      let range = '';
      let { startDate, endDate } = element;

      const planTimezone = getPlanTimezone(directSellingSettings);
      if (planTimezone) {
        // The picker works in the plan's timezone (BRAVON-6325): interpret the
        // picked calendar days as day boundaries in that timezone, matching
        // the preset ranges and the monthly closing.
        startDate = moment
          .tz(startDate.format('YYYY-MM-DD'), planTimezone)
          .startOf('day');
        endDate = moment
          .tz(endDate.format('YYYY-MM-DD'), planTimezone)
          .endOf('day');
      } else {
        // Making sure that the start/end date is what is passed on onChange
        startDate = dateWithoutTimeZone(startDate);
        endDate = dateWithoutTimeZone(endDate);
        startDate = startDate.startOf('day');
        endDate = endDate.endOf('day');
      }

      if (endDate.isSame(startDate, 'day')) {
        range = startDate.format('YYYY MMM DD');
      } else {
        range = `${startDate.format('YYYY MMM DD')} - ${endDate.format(
          'YYYY MMM DD',
        )}`;
      }
      picker.find('.m-input').val(range);

      console.log(`startDate.valueOf(${startDate}): `, startDate.valueOf());
      console.log(`endDate.valueOf(${endDate}): `, endDate.valueOf());
      onChange({ startDate: startDate.valueOf(), endDate: endDate.valueOf() });
    });

    picker.on('cancel.daterangepicker', () => {
      picker.find('.m-input').val('');

      onClear();
    });
  }

  componentDidUpdate(prevProps) {
    const { id, startDate, endDate } = this.props;
    if (startDate !== prevProps.startDate && endDate !== prevProps.endDate) {
      if (!startDate && !endDate) {
        $(`#${id}`).find('.m-input').val('');
      }
    }
  }

  render() {
    const { t, id, placeholder, directSellingSettings } = this.props;
    const planTimezone = getPlanTimezone(directSellingSettings);

    return (
      <div className="m-input-icon pull-right" id={id}>
        <input
          type="text"
          className="form-control m-input"
          placeholder={placeholder}
        />
        <span className="m-input-icon__icon m-input-icon__icon--right">
          <span>
            <i className="la la-calendar-check-o" />
          </span>
        </span>
        {planTimezone && (
          <small className="m--font-info d-block text-right">
            {`${t('dateRange_timezone')}: ${planTimezone} (GMT${moment
              .tz(planTimezone)
              .format('Z')})`}
          </small>
        )}
      </div>
    );
  }
}

DateRange.propTypes = {
  /** Translation function */
  t: PropTypes.func.isRequired,
  /** Id of the component */
  id: PropTypes.string,
  /** Initial values */
  startDate: PropTypes.number,
  endDate: PropTypes.number,
  /** Placeholder of the component */
  placeholder: PropTypes.string,
  /** Handler for date change */
  onChange: PropTypes.func.isRequired,
  /** Handler for date clear */
  onClear: PropTypes.func.isRequired,
  /** Whether the input is required in the form */
  timePicker: PropTypes.bool,
  customRanges: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.number)),
  opens: PropTypes.string,
  directSellingSettings: PropTypes.shape({
    salesIntegration: PropTypes.bool,
    VAIntegration: PropTypes.bool,
    recurrence: PropTypes.string,
    schedule: PropTypes.shape({
      recurrenceStartDay: PropTypes.number,
      recurrenceStartHour: PropTypes.number,
      timezone: PropTypes.string,
    }),
  }),
};

DateRange.defaultProps = {
  id: 'dateRangePicker',
  startDate: undefined,
  endDate: undefined,
  placeholder: undefined,
  timePicker: false,
  customRanges: undefined,
  opens: 'right',
  directSellingSettings: undefined,
};

export default translate(DateRange);
